/** tm/components/GovFeedLearnDrawer.tsx — 「AI 학습」 서랍 (2026-09-30 추가 확정 3)
 *
 *  지도사가 승인하면서 고친 칸이 쌓이면(교정) → 반복 패턴을 한 줄 규칙으로 정리해 다음 분석에 먼저 적용한다.
 *  · 칸별 정확도: 최근 승인 N건에서 AI 값을 그대로 둔 비율 (낮은 칸부터)
 *  · 규칙: 켜기/끄기 · 글 고치기 · 지우기 · 직접 추가 (배운 규칙을 고치거나 끄면 다시 학습해도 남는다)
 *  · [지금 다시 학습] · [새 규칙으로 미승인 건 다시 분석] (하루 분석 한도 안에서)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { C, T, R, Badge, Btn, PBar } from './ui';
import { BUCKETS, USAGE_KIND, fmtUsd, fmtTok } from './GovFeedParts';
import { BudgetBlock } from './GovFeedBudget';
import * as api from '../api';
import type { GovFeedRulesInfo, GovFeedRule, GovFeedStats, GovFeedBucket } from '../types';

/** 등록 payload 칸 → 화면 이름 */
export const FIELD_LABEL: Record<string, string> = {
  name: '사업명', report_name: '리포트 사업명', agency: '수행기관', amount: '지원금액', amount_max_manwon: '금액(숫자)', category: '분류',
  region: '지역', region_detail: '시군구', apply_start: '신청 시작일', deadline: '마감일', first_come: '선착순', recur_yearly: '매년 반복',
  target_biz_types: '업종', target_company_scale: '기업규모', target_startup: '창업 여부', target_ceo_age_min: '대표 나이(하한)',
  target_ceo_age_max: '대표 나이(상한)', target_sales_op: '연매출(조건)', target_sales_eok: '연매출', target_emp_op: '근로자(조건)',
  target_emp_n: '상시근로자', target_yrs_min: '업력(최소)', target_yrs_max: '업력(최대)', want_patent: '특허 가점', want_tm: '상표 가점',
  subs: '세부업종', targets: '전용 대상', tech: '기술 기반', bizx: '업종 제외',   // 하위 업종 · 전용 대상 판정 원칙(2026-10-05o) — 공고 분석 · AI 다시 읽기 둘 다 먼저 적용
};
/** 규칙 글 길이 — 서버 learn.RULE_MAX_CHARS 와 같다 (2026-10-05o 300 → 500 · 판정 원칙은 길다) */
const RULE_MAX = 500;

const errMsg = (e: unknown, fb: string) => (e instanceof Error && e.message ? e.message : fb);
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
function fmtAt(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (x: number) => String(x).padStart(2, '0');
  return `${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
const secTitle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'baseline', gap: 6 };
const inp: React.CSSProperties = { height: 34, padding: '0 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', boxSizing: 'border-box', background: C.white };
const smBtn: React.CSSProperties = { height: 26, padding: '0 9px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: T.xs, color: C.sub, whiteSpace: 'nowrap' };

function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)}
      style={{ width: 32, height: 18, borderRadius: 9, border: 'none', padding: 2, cursor: disabled ? 'default' : 'pointer', background: on ? C.accent : C.borderDk, flexShrink: 0, transition: 'background 0.15s', opacity: disabled ? 0.6 : 1 }}>
      <span style={{ display: 'block', width: 14, height: 14, borderRadius: 7, background: C.white, transform: `translateX(${on ? 14 : 0}px)`, transition: 'transform 0.15s' }} />
    </button>
  );
}

function RuleRow({ rule, onSaved, onDeleted, onError }: {
  rule: GovFeedRule; onSaved: (r: GovFeedRule) => void; onDeleted: (id: number) => void; onError: (m: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(rule.text);
  const [field, setField] = useState(rule.field || '');
  const [busy, setBusy] = useState(false);
  const [askDel, setAskDel] = useState(false);
  useEffect(() => { if (!editing) { setText(rule.text); setField(rule.field || ''); } }, [rule, editing]);

  async function put(patch: { text?: string; field?: string | null; enabled?: boolean }) {
    setBusy(true);
    try { const r = await api.updateGovFeedRule(rule.id, patch); if (r.data) onSaved(r.data.rule); setEditing(false); }
    catch (e) { onError(errMsg(e, '저장 실패')); }
    finally { setBusy(false); }
  }
  async function del() {
    setBusy(true);
    try { await api.deleteGovFeedRule(rule.id); onDeleted(rule.id); }
    catch (e) { onError(errMsg(e, '삭제 실패')); setBusy(false); }
  }

  return (
    <div style={{ display: 'flex', gap: 10, padding: '10px 12px', borderTop: `1px solid ${C.border}`, background: rule.enabled ? C.white : C.bgAlt }}>
      <div style={{ paddingTop: 2 }}><Toggle on={rule.enabled} disabled={busy} onChange={v => put({ enabled: v })} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <>
            <textarea autoFocus value={text} maxLength={RULE_MAX} onChange={e => setText(e.target.value)} rows={text.length > 160 ? 5 : 2}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void put({ text, field: field || null }); } if (e.key === 'Escape') setEditing(false); }}
              style={{ ...inp, width: '100%', height: 'auto', padding: '7px 10px', lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
              <select value={field} onChange={e => setField(e.target.value)} style={{ ...inp, height: 28, fontSize: T.xs, padding: '0 6px' }}>
                <option value="">칸 지정 안 함</option>
                {Object.entries(FIELD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <span style={{ flex: 1 }} />
              <button onClick={() => setEditing(false)} style={smBtn}>취소</button>
              <button disabled={busy || !text.trim()} onClick={() => put({ text, field: field || null })} style={{ ...smBtn, background: C.accent, color: C.white, border: 'none', fontWeight: 700 }}>저장</button>
            </div>
          </>
        ) : (
          <div onClick={() => setEditing(true)} title="눌러서 고치기" style={{ fontSize: T.sm, lineHeight: 1.55, color: rule.enabled ? C.text : C.muted, cursor: 'text', wordBreak: 'keep-all' }}>{rule.text}</div>
        )}
        {!editing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
            {rule.field && <Badge bg={C.accentLt} color={C.accent} style={{ padding: '1px 7px', fontSize: 10.5 }}>{FIELD_LABEL[rule.field] || rule.field}</Badge>}
            <Badge bg={rule.source === 'manual' ? C.purpleLt : C.bg} color={rule.source === 'manual' ? C.purple : C.sub} style={{ padding: '1px 7px', fontSize: 10.5 }}>{rule.source === 'manual' ? '직접' : '배움'}</Badge>
            {rule.key && <span style={{ fontSize: T.xs, color: C.muted }} title="하위 업종 · 전용 대상 판정 원칙 — 공고 분석과 「AI 다시 읽기」가 먼저 적용합니다. 고치면 배포해도 그대로 남습니다">판정 원칙{rule.edited ? ' · 고침' : ''}</span>}
            {rule.source === 'learned' && rule.edited && <span style={{ fontSize: T.xs, color: C.muted }}>고침 · 재학습해도 유지</span>}
            {rule.hits > 0 && <span style={{ fontSize: T.xs, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>근거 교정 {rule.hits}건</span>}
            <span style={{ flex: 1 }} />
            {askDel ? (
              <>
                <span style={{ fontSize: T.xs, color: C.red }}>지울까요?</span>
                <button onClick={() => setAskDel(false)} style={smBtn}>취소</button>
                <button disabled={busy} onClick={del} style={{ ...smBtn, color: C.red, borderColor: `${C.red}55` }}>삭제</button>
              </>
            ) : <button onClick={() => setAskDel(true)} style={{ ...smBtn, border: 'none', background: 'transparent', color: C.muted }} aria-label="규칙 지우기">삭제</button>}
          </div>
        )}
      </div>
    </div>
  );
}

interface Props {
  stats: GovFeedStats | null;
  onClose: () => void;
  onToast: (m: string) => void;
  /** 다시 분석을 걸었으면 현황·목록을 새로 */
  onRequeued: () => void;
  onInfo?: (info: GovFeedRulesInfo) => void;
  onEditBudget?: () => void;
}

export default function GovFeedLearnDrawer({ stats, onClose, onToast, onRequeued, onInfo, onEditBudget }: Props) {
  const [info, setInfo] = useState<GovFeedRulesInfo | null>(null);
  const [err, setErr] = useState('');
  const [learning, setLearning] = useState(false);
  const [newText, setNewText] = useState('');
  const [newField, setNewField] = useState('');
  const [adding, setAdding] = useState(false);
  const [askRe, setAskRe] = useState(false);
  const [reBuckets, setReBuckets] = useState<Set<GovFeedBucket>>(new Set<GovFeedBucket>(['ready', 'check', 'maybe']));
  const [reBusy, setReBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.getGovFeedRules(); if (r.data) { setInfo(r.data); onInfo?.(r.data); setErr(''); } }
    catch (e) { setErr(errMsg(e, '학습 현황을 불러오지 못했습니다')); }
  }, [onInfo]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return; // 입력칸의 Esc 는 그 칸이 처리(편집 취소)
      e.stopPropagation(); if (askRe) setAskRe(false); else onClose();
    };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [onClose, askRe]);

  const setRules = (fn: (rs: GovFeedRule[]) => GovFeedRule[]) => setInfo(i => (i ? { ...i, rules: fn(i.rules) } : i));

  // 학습은 AI 호출이라 1분 넘게 걸릴 수 있다 — 서버가 뒤에서 계속하면(background) 끝날 때까지 현황을 다시 읽는다 (2026-10-03 「날짜가 안 바뀐다」)
  const pollRef = useRef<number | null>(null);
  useEffect(() => () => { if (pollRef.current) window.clearTimeout(pollRef.current); }, []);
  // 열었을 때 이미 뒤에서 학습 중이면(관심 다시 분석 등) 끝날 때까지 현황을 다시 읽는다
  useEffect(() => {
    if (!info?.learning || learning) return;
    const t = window.setTimeout(() => { void load(); }, 5000);
    return () => window.clearTimeout(t);
  }, [info, learning, load]);
  const doneToast = (s: { feedbackUsed?: number; learned?: number; kept?: number } | null | undefined) =>
    onToast(`학습 완료 — 교정 ${s?.feedbackUsed ?? 0}건에서 규칙 ${s?.learned ?? 0}개${s?.kept ? ` · 지도사 규칙 ${s.kept}개 유지` : ''}`);
  async function relearn() {
    setLearning(true);
    const before = info?.lastLearnedAt || null;
    try {
      const r = await api.learnGovFeedRules();
      if (!r.data?.background) { doneToast(r.data); await load(); setLearning(false); return; }
      onToast('학습 중 — AI 가 교정 기록을 읽고 있어요 (1~2분). 끝나면 여기 「마지막 학습」이 바뀝니다');
      const started = Date.now();
      const tick = async () => {
        let x: GovFeedRulesInfo | null = null;
        try { const g = await api.getGovFeedRules(); x = g.data || null; if (x) { setInfo(x); onInfo?.(x); } } catch { /* 다음에 다시 */ }
        if (x && !x.learning) {
          setLearning(false);
          if (x.lastLearnError && (!x.lastLearnedAt || x.lastLearnedAt === before)) onToast(`학습 실패 — ${x.lastLearnError.error || '알 수 없는 오류'}`);
          else doneToast(x.lastLearnStats);
          return;
        }
        if (Date.now() - started > 5 * 60 * 1000) { setLearning(false); onToast('학습이 오래 걸립니다 — 잠시 뒤 다시 열어 확인해 주세요'); return; }
        pollRef.current = window.setTimeout(() => { void tick(); }, 4000);
      };
      pollRef.current = window.setTimeout(() => { void tick(); }, 4000);
    } catch (e) { onToast(errMsg(e, '학습 실패')); setLearning(false); void load(); }
  }

  async function add() {
    if (!newText.trim() || adding) return;
    setAdding(true);
    try {
      const r = await api.createGovFeedRule(newText.trim(), newField || null);
      if (r.data) setRules(rs => [r.data!.rule, ...rs]);
      setNewText(''); setNewField('');
    } catch (e) { onToast(errMsg(e, '추가 실패')); }
    finally { setAdding(false); }
  }

  async function reanalyze() {
    setReBusy(true);
    try {
      const r = await api.reanalyzeOpenGovFeed(Array.from(reBuckets));
      const d = r.data;
      onToast(d ? `${d.pickOnly ? '★ 공고 ' : ''}${d.queued.toLocaleString()}건 다시 분석 대기열에 (일괄 · 50% 할인)${d.capped ? ` · 한도로 ${(d.matched - d.queued).toLocaleString()}건은 내일 다시` : ''}` : '다시 분석을 걸었어요');
      setAskRe(false);
      onRequeued();
    } catch (e) { onToast(errMsg(e, '다시 분석 실패')); }
    finally { setReBusy(false); }
  }

  const acc = info?.accuracy;
  const fields = acc ? Object.entries(acc.byField).filter(([, v]) => v.n > 0).sort((a, b) => a[1].agree / a[1].n - b[1].agree / b[1].n) : [];
  const overall = acc?.overall != null ? Math.round(acc.overall * 100) : null;
  const manual = info?.rules.filter(r => r.source === 'manual').length ?? 0;
  const learned = (info?.rules.length ?? 0) - manual;
  const b = stats?.counts.buckets;
  const reCount = b ? Array.from(reBuckets).reduce((s, k) => s + (b[k] || 0), 0) : null;
  const left = stats && !stats.dailyUnlimited ? Math.max(0, stats.dailyMax - stats.analyzedToday) : null; // 상한 없음이면 null (총예산이 막는다)
  const isLearning = learning || !!info?.learning;
  const pickMode = stats?.scope?.mode === 'pick';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.28)' }} />
      <aside role="dialog" aria-label="AI 학습" style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(460px, 100vw)', background: C.white, boxShadow: '-8px 0 30px rgba(17,24,39,0.18)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontSize: T.lg, fontWeight: 700, color: C.text }}>AI 학습</span>
          <span style={{ fontSize: T.xs, color: C.muted }}>고친 방식을 배워 다음 분석에 먼저 적용</span>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} aria-label="닫기" style={{ ...smBtn, width: 28, height: 28, padding: 0, fontSize: T.sm }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px 24px' }}>
          {err && <div style={{ fontSize: T.sm, color: C.red, marginBottom: 12 }}>{err}</div>}

          {/* 요약 */}
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '14px 16px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.bgAlt }}>
            <div style={{ textAlign: 'center', minWidth: 76 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: overall == null ? C.muted : overall >= 85 ? C.green : overall >= 70 ? C.accent : '#B45309', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{overall == null ? '—' : `${overall}%`}</div>
              <div style={{ fontSize: T.xs, color: C.sub, marginTop: 3 }}>칸 일치율</div>
            </div>
            <div style={{ flex: 1, minWidth: 0, fontSize: T.xs, color: C.sub, lineHeight: 1.7 }}>
              <div>최근 승인 <b style={{ color: C.text, fontVariantNumeric: 'tabular-nums' }}>{(acc?.recentN ?? 0).toLocaleString()}</b>건 기준 · 교정 누적 <b style={{ color: C.text, fontVariantNumeric: 'tabular-nums' }}>{(info?.feedbackCount ?? 0).toLocaleString()}</b>건</div>
              <div>마지막 학습 {isLearning ? <b style={{ color: C.accent }}>학습 중…</b> : info?.lastLearnedAt ? fmtAt(info.lastLearnedAt) : '아직 없음'} · 교정 {info?.learnEvery ?? 20}건마다 자동</div>
              {!isLearning && info?.lastLearnError && (
                <div title={info.lastLearnError.error} style={{ color: C.red, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {fmtAt(info.lastLearnError.at)} 학습 실패 — {info.lastLearnError.error || '알 수 없는 오류'}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <Btn variant="secondary" onClick={relearn} disabled={isLearning || !info} style={{ flex: 1, fontSize: T.sm }}>{isLearning ? '학습 중…' : '지금 다시 학습'}</Btn>
            <Btn onClick={() => setAskRe(true)} disabled={!info || stats?.aiConfigured === false} style={{ flex: 1.4, fontSize: T.sm, whiteSpace: 'nowrap' }}>새 규칙으로 다시 분석</Btn>
          </div>

          {/* 칸별 정확도 */}
          <div style={{ marginTop: 22 }}>
            <div style={secTitle}>칸별 정확도 <span style={{ fontWeight: 400, color: C.muted }}>AI 값을 그대로 둔 비율 · 낮은 칸부터</span></div>
            {!fields.length ? (
              <div style={{ fontSize: T.sm, color: C.muted, padding: '6px 0' }}>승인한 공고가 쌓이면 여기에 보입니다</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr 70px', alignItems: 'center', rowGap: 8, columnGap: 10 }}>
                {fields.map(([k, v]) => {
                  const p = pct(v.agree, v.n);
                  return [
                    <span key={`${k}l`} style={{ fontSize: T.sm, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{FIELD_LABEL[k] || k}</span>,
                    <PBar key={`${k}b`} value={p} h={8} color={p >= 90 ? C.green : p >= 70 ? C.accent : '#D97706'} />,
                    <span key={`${k}n`} style={{ fontSize: T.xs, color: C.sub, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}><b style={{ color: C.text }}>{p}%</b> · {v.n}</span>,
                  ];
                })}
              </div>
            )}
          </div>

          {/* AI 예산 */}
          {stats?.budget && (
            <div style={{ marginTop: 24 }}>
              <div style={secTitle}>AI 예산 <span style={{ fontWeight: 400, color: C.muted }}>한도를 넘는 요청은 보내지 않습니다</span></div>
              <BudgetBlock b={stats.budget} onEdit={() => onEditBudget?.()} />
            </div>
          )}

          {/* AI 사용량 */}
          {stats?.usage && (() => {
            const u = stats.usage;
            const kinds = Array.from(new Set(['screen', 'analyze', 'analyze_batch', 'learn', ...Object.keys(u.today.byKind || {}), ...Object.keys(u.total.byKind || {})]));
            const cell = (v?: { calls: number; estUsd: number }) => (v && v.calls
              ? <><span style={{ color: C.text, fontWeight: 600 }}>{fmtUsd(v.estUsd)}</span> <span style={{ color: C.muted }}>{v.calls.toLocaleString()}회</span></>
              : <span style={{ color: C.muted }}>—</span>);
            const th: React.CSSProperties = { fontSize: T.xs, fontWeight: 600, color: C.sub, textAlign: 'right', padding: '6px 10px', background: C.bgAlt, borderBottom: `1px solid ${C.border}` };
            const td: React.CSSProperties = { fontSize: T.xs, textAlign: 'right', padding: '7px 10px', borderBottom: `1px solid ${C.border}`, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
            return (
              <div style={{ marginTop: 24 }}>
                <div style={secTitle}>AI 사용량 <span style={{ fontWeight: 400, color: C.muted }}>추정 비용 · 오늘 분석 {stats.analyzedToday.toLocaleString()}{stats.dailyUnlimited ? '건' : `/${stats.dailyMax.toLocaleString()}건`}</span></div>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, border: `1px solid ${C.border}`, borderRadius: R.md, overflow: 'hidden' }}>
                  <thead><tr><th style={{ ...th, textAlign: 'left' }}>용도</th><th style={th}>오늘</th><th style={th}>누적</th></tr></thead>
                  <tbody>
                    {kinds.map(k => (
                      <tr key={k}><td style={{ ...td, textAlign: 'left', color: C.text }}>{USAGE_KIND[k] || k}</td><td style={td}>{cell(u.today.byKind?.[k])}</td><td style={td}>{cell(u.total.byKind?.[k])}</td></tr>
                    ))}
                    <tr>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: C.text, borderBottom: 'none' }}>합계</td>
                      <td style={{ ...td, borderBottom: 'none' }}><b style={{ color: C.text }}>{fmtUsd(u.today.estUsd)}</b></td>
                      <td style={{ ...td, borderBottom: 'none' }}><b style={{ color: C.text }}>{fmtUsd(u.total.estUsd)}</b></td>
                    </tr>
                  </tbody>
                </table>
                <div style={{ fontSize: T.xs, color: C.muted, marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>
                  누적 토큰 입력 {fmtTok(u.total.inTok)} · 출력 {fmtTok(u.total.outTok)} · 모델별 공시 단가로 계산한 추정치
                </div>
              </div>
            );
          })()}

          {/* 규칙 */}
          <div style={{ marginTop: 24 }}>
            <div style={secTitle}>규칙 <span style={{ fontWeight: 400, color: C.muted }}>배움 {learned} · 직접 {manual} — 켜진 규칙을 분석 · AI 다시 읽기 때 먼저 적용</span></div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <input value={newText} maxLength={RULE_MAX} onChange={e => setNewText(e.target.value)} placeholder="예: 「소공인」 대상이면 기업규모는 소상공인"
                onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); void add(); } }}
                style={{ ...inp, flex: 1, minWidth: 0 }} />
              <select value={newField} onChange={e => setNewField(e.target.value)} style={{ ...inp, width: 104, fontSize: T.xs, padding: '0 6px' }} aria-label="칸">
                <option value="">칸 무관</option>
                {Object.entries(FIELD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <Btn onClick={add} disabled={!newText.trim() || adding} style={{ fontSize: T.sm, padding: '0 12px', height: 34 }}>추가</Btn>
            </div>
            <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, overflow: 'hidden' }}>
              {info && !info.rules.length && <div style={{ padding: '18px 12px', fontSize: T.sm, color: C.muted, textAlign: 'center' }}>아직 규칙이 없습니다 — 교정이 쌓이면 자동으로 생깁니다</div>}
              {!info && !err && <div style={{ padding: '18px 12px', fontSize: T.sm, color: C.muted, textAlign: 'center' }}>불러오는 중…</div>}
              <div style={{ marginTop: -1 }}>
                {info?.rules.map(r => (
                  <RuleRow key={r.id} rule={r} onError={onToast}
                    onSaved={nr => setRules(rs => rs.map(x => (x.id === nr.id ? nr : x)))}
                    onDeleted={id => setRules(rs => rs.filter(x => x.id !== id))} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 다시 분석 확인 */}
        {askRe && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div role="dialog" aria-modal="true" style={{ width: '100%', maxWidth: 380, background: C.white, borderRadius: R.lg, border: `1px solid ${C.border}`, boxShadow: '0 10px 30px rgba(17,24,39,0.18)', padding: '18px 18px 14px' }}>
              <div style={{ fontSize: T.md, fontWeight: 700, color: C.text }}>새 규칙으로 다시 분석</div>
              <div style={{ fontSize: T.sm, color: C.sub, lineHeight: 1.6, marginTop: 6 }}>아직 승인하지 않은 AI 완료 공고를 최신 등록 순으로 다시 분석합니다. 표에서 고친 값은 그대로 남습니다.</div>
              {pickMode && (
                <div style={{ fontSize: T.xs, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: R.sm, padding: '6px 9px', marginTop: 8, lineHeight: 1.5 }}>
                  심층 분석이 「★ 체크한 것만」이라 <b>★ 한 공고만</b> 다시 분석합니다 (아래 건수는 탭 전체)
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '14px 0' }}>
                {BUCKETS.filter(x => x.k !== 'pending' && x.k !== 'unfit' && x.k !== 'past').map(x => (
                  <label key={x.k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: T.sm, color: C.text, cursor: 'pointer' }}>
                    <input type="checkbox" checked={reBuckets.has(x.k)} style={{ accentColor: C.accent }}
                      onChange={e => setReBuckets(s => { const nx = new Set(s); if (e.target.checked) nx.add(x.k); else nx.delete(x.k); return nx; })} />
                    <span style={{ width: 7, height: 7, borderRadius: 4, background: x.dot }} />{x.label}
                    <span style={{ marginLeft: 'auto', fontSize: T.xs, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{b ? `${(b[x.k] || 0).toLocaleString()}건` : ''}</span>
                  </label>
                ))}
              </div>
              {reCount != null && left != null && (
                <div style={{ fontSize: T.xs, color: reCount > left ? '#92400E' : C.muted, marginBottom: 12 }}>
                  오늘 남은 분석 한도 {left.toLocaleString()}건{reCount > left ? ` — ${(reCount - left).toLocaleString()}건은 한도 때문에 이번엔 빠집니다` : ''}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setAskRe(false)} style={{ flex: 1, height: 38, borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>취소</button>
                <button onClick={reanalyze} disabled={reBusy || !reBuckets.size} style={{ flex: 2, height: 38, borderRadius: R.md, border: 'none', background: `linear-gradient(135deg,${C.accent},${C.primary})`, color: C.white, fontSize: T.md, fontWeight: 700, cursor: 'pointer', opacity: reBusy || !reBuckets.size ? 0.6 : 1 }}>
                  {reBusy ? '거는 중…' : `다시 분석${reCount != null ? ` (${Math.min(reCount, left ?? reCount).toLocaleString()}건)` : ''}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
